import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@mibodega/db-contract';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    'Supabase no configurado. Variables VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY faltantes.',
  );
}

/**
 * Cliente Supabase tipado con el schema generado.
 * Ver `pnpm db:types` y `docs/CONTRACTS.md` § 1.
 *
 * Esto evita bugs como el de sprint 55: cuando una migración agrega una columna
 * nueva, el `select('*')` la trae como `unknown` hasta que regeneremos tipos.
 * El typecheck detecta queries a columnas inexistentes.
 */
export const supabase: SupabaseClient<Database> = createClient<Database>(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-anon-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
    },
  },
);

/**
 * Extrae el `organizationId` del JWT de Supabase Auth.
 * Se espera que el claim esté en `app_metadata.org_id` o `user_metadata.org_id`.
 *
 * Sentinel `*` (org_id = '*') indica super-admin (auditor cross-org).
 * En ese caso, `allOrgs: true` permite saltarse el filtro por organización.
 *
 * Si no existe, devuelve `null` (el caller debe decidir qué hacer).
 */
export interface OrganizationClaim {
  organizationId: string;
  allOrgs: boolean;
  role?: string;
}

export async function getOrganizationIdFromSession(): Promise<OrganizationClaim | null> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return null;

    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as Record<
      string,
      unknown
    >;

    const appMeta = (decoded.app_metadata ?? {}) as Record<string, unknown>;
    const userMeta = (decoded.user_metadata ?? {}) as Record<string, unknown>;
    const orgId = (appMeta.org_id as string | undefined) ?? (userMeta.org_id as string | undefined);

    if (!orgId) return null;

    return {
      organizationId: orgId,
      allOrgs: orgId === '*',
      role: appMeta.role as string | undefined,
    };
  } catch (err) {
    console.warn('getOrganizationIdFromSession:', err);
    return null;
  }
}
